import * as React from 'react';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { UndoRedo } from '@tiptap/extensions';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view';
import { Editor, EditorContent, Extension, JSONContent, useEditor } from '@tiptap/react';
import Suggestion, { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import { tokenAlternation } from '../../core/utils/chipEditor.ts';
import classnames from '../../core/utils/classnames.tsx';
import { PROMPT_PURPLE_CLASS } from '../../products/voice_agents/components/VoiceAgentPromptEditor.tsx';
import { isWordName } from './fineTuning/rules.ts';

// the "/" menu's size; w-80 and h-72
const MENU_WIDTH = 320;

export type PromptFieldType = 'output' | 'input';

export const FIELD_COLOR_CLASS: Record<PromptFieldType, string> = {
    input: PROMPT_PURPLE_CLASS,
    output: 'bg-surface-brand-medium-default text-content-brand-strong',
};

const CHIP_BASE_CLASS = 'rounded px-1 py-0.5 text-xs font-medium mx-0.5';
const MARK_CLASS =
    'cursor-pointer bg-surface-brand-weak-default underline decoration-dashed decoration-line-brand-default underline-offset-[3px]';

export interface PromptMenuItem {
    name: string;
    type: PromptFieldType;
    label: string;
    section: string;
}

export interface PromptMark {
    key: string;
    // needs the g flag
    pattern: RegExp;
}

export interface PromptMarkHit {
    key: string;
    range: Range;
}

export interface PromptEditorHandle {
    insert: (name: string) => void;
}

export interface PromptEditorProps {
    value: string;
    onValueChange: (value: string) => void;
    outputFields: string[];
    inputFields: string[];
    menuItems: PromptMenuItem[];
    onInsertField: (name: string, type: PromptFieldType) => void;
    marks?: PromptMark[];
    onMarkClick?: (hit: PromptMarkHit | null) => void;
    placeholder?: string;
    ref?: React.Ref<PromptEditorHandle>;
}

interface Highlights {
    chipClassByName: Map<string, string>;
    marks: PromptMark[];
}

interface HighlightState extends Highlights {
    decorations: DecorationSet;
}

interface EditorCallbacks extends Pick<PromptEditorProps, 'onValueChange' | 'onInsertField' | 'onMarkClick'> {
    onMenuKeyDown: (event: KeyboardEvent) => boolean;
}

interface MenuState {
    suggestion: SuggestionProps<never, PromptMenuItem>;
    top: number;
    left: number;
}

const NO_MARKS: PromptMark[] = [];

const highlightKey = new PluginKey<HighlightState>('promptHighlights');

const getDecorations = (doc: ProseMirrorNode, { chipClassByName, marks }: Highlights) => {
    const chipPattern = chipClassByName.size
        ? new RegExp(tokenAlternation([...chipClassByName.keys()], isWordName), 'g')
        : null;
    const decorations: Decoration[] = [];
    doc.descendants((node, pos) => {
        if (!node.isTextblock) return true;
        const text = node.textContent;
        const start = pos + 1;
        const chips = chipPattern
            ? [...text.matchAll(chipPattern)].map((match) => ({
                  name: match[0],
                  from: match.index,
                  to: match.index + match[0].length,
              }))
            : [];
        chips.forEach(({ name, from, to }) =>
            decorations.push(
                Decoration.inline(start + from, start + to, { class: chipClassByName.get(name) }, { chip: name })
            )
        );
        marks.forEach(({ key, pattern }) => {
            for (const match of text.matchAll(pattern)) {
                const from = match.index;
                const to = from + match[0].length;
                if (chips.some((chip) => chip.from < to && from < chip.to)) continue;
                decorations.push(Decoration.inline(start + from, start + to, { class: MARK_CLASS }, { mark: key }));
            }
        });
        return false;
    });
    return DecorationSet.create(doc, decorations);
};

const getDomRange = (view: EditorView, from: number, to: number) => {
    const start = view.domAtPos(from);
    const end = view.domAtPos(to);
    const range = window.document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
};

const removeChip = (editor: Editor, side: 'before' | 'after') => {
    const { selection } = editor.state;
    if (!selection.empty) return false;
    const chip = highlightKey
        .getState(editor.state)
        ?.decorations.find(selection.from, selection.from, (spec) => !!spec.chip)
        .find(({ from, to }) => (side === 'before' ? to : from) === selection.from);
    return chip ? editor.commands.deleteRange({ from: chip.from, to: chip.to }) : false;
};

const insertName = (editor: Editor, { from, to }: { from: number; to: number }, name: string) => {
    const { doc } = editor.state;
    const charBefore = doc.textBetween(Math.max(0, from - 1), from, '\n');
    const charAfter = doc.textBetween(to, Math.min(doc.content.size, to + 1), '\n');
    const spaceBefore = charBefore && !/\s/.test(charBefore) ? ' ' : '';
    const spaceAfter = charAfter && /\s/.test(charAfter) ? '' : ' ';
    editor
        .chain()
        .focus()
        .insertContentAt({ from, to }, { type: 'text', text: `${spaceBefore}${name}${spaceAfter}` })
        .run();
};

const toDoc = (text: string): JSONContent => ({
    type: 'doc',
    content: text
        .split('\n')
        .map((line) => ({ type: 'paragraph', content: line ? [{ type: 'text', text: line }] : [] })),
});

const toText = (editor: Editor) => editor.getText({ blockSeparator: '\n' });

// The marks in front of a name in the "/" menu and in the add search
export const PromptFieldMark = ({ type }: { type: PromptFieldType }) => {
    const { t } = useTranslation('config');
    return (
        <span
            className={classnames(
                'flex-none rounded px-1 py-0.5 text-[0.625em] font-medium uppercase',
                FIELD_COLOR_CLASS[type]
            )}
        >
            {type === 'input' ? t('configPage.fineTuning.inputMark') : t('configPage.fineTuning.outputMark')}
        </span>
    );
};

// Instructions with the names of input data and output fields as chips. Only names in `inputFields` and
// `outputFields` are chips; "/" opens a menu of `menuItems`, grouped by their section. The editor has no frame of its
// own, its caller draws one.
export const PromptEditor = ({
    value,
    onValueChange,
    outputFields,
    inputFields,
    menuItems,
    onInsertField,
    marks = NO_MARKS,
    onMarkClick,
    placeholder,
    ref,
}: PromptEditorProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const lastValueRef = useRef(value);
    const wasFocusedRef = useRef(false);
    const [menu, setMenu] = useState<MenuState | null>(null);
    const [selectedMenuIndex, setSelectedMenuIndex] = useState(0);

    const callbacksRef = useRef<EditorCallbacks>({
        onValueChange,
        onInsertField,
        onMarkClick,
        onMenuKeyDown: () => false,
    });
    const filteredItems = useMemo(() => {
        const lower = menu?.suggestion.query.toLowerCase() ?? '';
        if (!lower) return menuItems;
        return menuItems.filter(
            (item) => item.name.toLowerCase().includes(lower) || item.label.toLowerCase().includes(lower)
        );
    }, [menuItems, menu?.suggestion.query]);
    callbacksRef.current = {
        onValueChange,
        onInsertField,
        onMarkClick,
        onMenuKeyDown: (event) => {
            if (!menu || !filteredItems.length) return false;
            if (event.key === 'ArrowDown') {
                setSelectedMenuIndex((index) => Math.min(index + 1, filteredItems.length - 1));
            } else if (event.key === 'ArrowUp') {
                setSelectedMenuIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter' || event.key === 'Tab') {
                menu.suggestion.command(filteredItems[selectedMenuIndex]);
            } else {
                return false;
            }
            return true;
        },
    };

    const [extensions] = useState(() => {
        const showMenu = (suggestion: SuggestionProps<never, PromptMenuItem>) => {
            const rect = suggestion.clientRect?.();
            const containerRect = containerRef.current?.getBoundingClientRect();
            setMenu({
                suggestion,
                top: rect && containerRect ? rect.bottom - containerRect.top + 4 : 0,
                left: rect && containerRect ? rect.left - containerRect.left : 0,
            });
            setSelectedMenuIndex(0);
        };
        return [
            Document,
            Paragraph,
            Text,
            UndoRedo,
            Extension.create({
                name: 'promptHighlights',
                addProseMirrorPlugins: () => [
                    new Plugin<HighlightState>({
                        key: highlightKey,
                        state: {
                            init: () => ({ chipClassByName: new Map(), marks: [], decorations: DecorationSet.empty }),
                            apply: (tr, state) => {
                                const highlights: Highlights = tr.getMeta(highlightKey) ?? state;
                                if (!tr.docChanged && highlights === state) return state;
                                return { ...highlights, decorations: getDecorations(tr.doc, highlights) };
                            },
                        },
                        props: {
                            decorations: (state) => highlightKey.getState(state)?.decorations,
                            handleClick: (view, pos) => {
                                const mark = highlightKey
                                    .getState(view.state)
                                    ?.decorations.find(pos, pos, (spec) => !!spec.mark)[0];
                                callbacksRef.current.onMarkClick?.(
                                    mark ? { key: mark.spec.mark, range: getDomRange(view, mark.from, mark.to) } : null
                                );
                                return false;
                            },
                        },
                    }),
                ],
                addKeyboardShortcuts() {
                    return {
                        Backspace: () => removeChip(this.editor, 'before'),
                        Delete: () => removeChip(this.editor, 'after'),
                    };
                },
            }),
            Extension.create({
                name: 'promptMenu',
                addProseMirrorPlugins() {
                    return [
                        Suggestion<never, PromptMenuItem>({
                            editor: this.editor,
                            char: '/',
                            allowedPrefixes: null,
                            command: ({ editor, range, props: item }) => {
                                insertName(editor, range, item.name);
                                callbacksRef.current.onInsertField(item.name, item.type);
                            },
                            render: () => ({
                                onStart: showMenu,
                                onUpdate: showMenu,
                                onExit: () => setMenu(null),
                                onKeyDown: ({ event }: SuggestionKeyDownProps) =>
                                    callbacksRef.current.onMenuKeyDown(event),
                            }),
                        }),
                    ];
                },
            }),
        ];
    });

    const editor = useEditor({
        extensions,
        content: toDoc(lastValueRef.current),
        editorProps: {
            attributes: {
                class: 'px-2 py-1.5 min-h-24 overflow-auto outline-none text-sm text-content-strong whitespace-pre-wrap break-words resize-y',
            },
        },
        onUpdate: ({ editor: updated }) => {
            const text = toText(updated);
            lastValueRef.current = text;
            callbacksRef.current.onValueChange(text);
        },
        onFocus: () => {
            wasFocusedRef.current = true;
        },
        onBlur: () => setMenu(null),
    });

    useEffect(() => {
        if (!editor || value === lastValueRef.current) return;
        lastValueRef.current = value;
        editor.commands.setContent(toDoc(value), { emitUpdate: false });
    }, [editor, value]);

    const chipClassByName = useMemo(() => {
        const map = new Map<string, string>();
        outputFields.forEach((field) => map.set(field, classnames(CHIP_BASE_CLASS, FIELD_COLOR_CLASS.output)));
        inputFields.forEach((field) => map.set(field, classnames(CHIP_BASE_CLASS, FIELD_COLOR_CLASS.input)));
        return map;
    }, [outputFields, inputFields]);

    useEffect(() => {
        if (!editor) return;
        const highlights: Highlights = { chipClassByName, marks };
        editor.view.dispatch(editor.state.tr.setMeta(highlightKey, highlights).setMeta('addToHistory', false));
    }, [editor, chipClassByName, marks]);

    // puts the name where the caret last was, or at the end when the editor wasn't used yet
    useImperativeHandle(
        ref,
        () => ({
            insert: (name) => {
                if (!editor) return;
                const end = Selection.atEnd(editor.state.doc).from;
                insertName(editor, wasFocusedRef.current ? editor.state.selection : { from: end, to: end }, name);
            },
        }),
        [editor]
    );

    return (
        <div ref={containerRef} className="relative">
            {!value && (
                <div className="absolute inset-0 px-2 py-1.5 text-sm text-content-weak pointer-events-none">
                    {placeholder}
                </div>
            )}
            <EditorContent editor={editor} />
            {menu && filteredItems.length > 0 && (
                <div
                    className="absolute z-50 w-80 max-h-72 bg-surface-weak border border-solid border-line-weak rounded-lg shadow-lg overflow-auto py-1"
                    style={{
                        top: menu.top,
                        // at the caret, moved left only as far as needed to stay inside the editor
                        left: Math.max(
                            0,
                            Math.min(menu.left, (containerRef.current?.clientWidth ?? MENU_WIDTH) - MENU_WIDTH)
                        ),
                    }}
                >
                    {filteredItems.map((item, index) => (
                        <React.Fragment key={`${item.type}-${item.name}`}>
                            {(index === 0 || filteredItems[index - 1].section !== item.section) && (
                                <div className="px-3 pb-1 pt-2 text-xs font-medium text-content-medium">
                                    {item.section}
                                </div>
                            )}
                            <div
                                className={classnames(
                                    'px-3 py-1.5 text-sm cursor-pointer flex items-center gap-2',
                                    index === selectedMenuIndex ? 'bg-surface-strong' : 'hover:bg-surface-strong'
                                )}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    menu.suggestion.command(item);
                                }}
                                onMouseEnter={() => setSelectedMenuIndex(index)}
                            >
                                <PromptFieldMark type={item.type} />
                                <div className="flex flex-col min-w-0">
                                    <div className="font-medium truncate">{item.label}</div>
                                    <div className="text-xs text-content-weak truncate">{item.name}</div>
                                </div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};
